/**
 * `BriefApprovalFolders` — the brief side of the brief/folder association.
 *
 * A brief is delivered into as many folders as the work needs, so this is a set
 * rather than a choice: the rows are the way back to each folder, the picker
 * adds one, and the cross releases one. A folder answers to a single brief, so
 * one already spoken for is not offered.
 *
 * The association is stored on the folder row, which is why every edit here
 * invalidates the folder caches rather than only the brief's own.
 */
import { useMemo } from 'react';
import type { BriefResponseDto } from '@inference.engine/workspace-api-client/web';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useUpdateBrief } from '@/entities/brief';
import { PROJECT_FOLDER_QUERY_KEYS, projectFoldersQuery } from '@/entities/project-folder';
import { notificationService } from '@/lib/services/notification.service';
import { ROUTES } from '@/shared/config/routes';
import { parseProblemDetail } from '@/shared/utils/problem-details';

/** Radix refuses an empty item value, and the picker never holds a selection. */
// eslint-disable-next-line i18next/no-literal-string -- a Select item value on the wire, never rendered
const ADD_PLACEHOLDER = 'add';

/** One request covers a project's root folders; the endpoint caps a page here. */
const FOLDER_PAGE_SIZE = 100;

export interface BriefApprovalFoldersProps {
  brief: BriefResponseDto;
  /**
   * Platform project id — the id the `projects/:platformProjectId/*` routes are
   * scoped to. `brief.projectId` is the internal id and 403s against them.
   */
  platformProjectId: string;
  triggerId?: string;
}

export function BriefApprovalFolders({
  brief,
  platformProjectId,
  triggerId,
}: BriefApprovalFoldersProps) {
  const { t } = useTranslation('projects');
  const queryClient = useQueryClient();

  // Root folders only: a subfolder inherits its parent's brief rather than
  // carrying one, so offering them would be offering a rejected write.
  const { data } = useQuery(projectFoldersQuery(platformProjectId, { pageSize: FOLDER_PAGE_SIZE }));

  const linked = brief.approvalFolders;
  const isReady = brief.status === 'submitted';
  const linkedIds = useMemo(() => new Set(linked.map((folder) => folder.id)), [linked]);

  const available = useMemo(
    () =>
      (data?.items ?? [])
        .filter((folder) => !linkedIds.has(folder.id))
        .filter((folder) => !folder.brief || folder.brief.id === brief.id),
    [data?.items, linkedIds, brief.id],
  );

  const { mutate, isPending } = useUpdateBrief({
    projectId: platformProjectId,
    briefId: brief.id,
    onSuccess: () => {
      // The write lands on folder rows, which this brief's own cache entry knows
      // nothing about — the folder list and every folder detail naming the
      // brief in its header are stale until they refetch.
      void queryClient.invalidateQueries({
        queryKey: PROJECT_FOLDER_QUERY_KEYS.allForProject(platformProjectId),
      });
    },
    onError: (error) => {
      notificationService.showError(
        parseProblemDetail(error, t('projectBriefDetail.briefApprovalFolders.failedToUpdate'))
          .detail,
      );
    },
  });

  const commit = (folderIds: string[]) => mutate({ body: { approvalFolderIds: folderIds } });

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-2" data-testid="brief-approval-folders">
        {linked.length > 0 && (
          <ul className="flex flex-col gap-1">
            {linked.map((folder) => (
              <li key={folder.id} className="flex min-w-0 items-center gap-1">
                <Link
                  to={ROUTES.PROJECT_FOLDER_DETAIL(platformProjectId, folder.id)}
                  title={folder.name}
                  className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-muted"
                  data-testid="brief-approval-folder-link"
                >
                  <FolderIcon className="size-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{folder.name}</span>
                </Link>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={isPending}
                      aria-label={t('projectBriefDetail.briefApprovalFolders.removeFolder', {
                        name: folder.name,
                      })}
                      data-testid="brief-approval-folder-remove"
                      onClick={() =>
                        commit(linked.filter((f) => f.id !== folder.id).map((f) => f.id))
                      }
                    >
                      <XIcon className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('projectBriefDetail.briefApprovalFolders.removeFolder', {
                      name: folder.name,
                    })}
                  </TooltipContent>
                </Tooltip>
              </li>
            ))}
          </ul>
        )}

        <Select
          value={ADD_PLACEHOLDER}
          onValueChange={(next) => commit([...linked.map((f) => f.id), next])}
          disabled={isPending || !isReady || available.length === 0}
        >
          <SelectTrigger
            id={triggerId}
            className="w-full"
            data-testid="brief-approval-folders-add-trigger"
            aria-label={t('projectBriefDetail.briefApprovalFolders.addFolder')}
          >
            {/* The trigger is an action, not a value: it never shows a
                selection, so the label is written rather than left to
                `placeholder`, which a held value would suppress. */}
            <SelectValue>
              {available.length === 0
                ? t('projectBriefDetail.briefApprovalFolders.noFoldersAvailable')
                : t('projectBriefDetail.briefApprovalFolders.addFolder')}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {available.map((folder) => (
              <SelectItem key={folder.id} value={folder.id}>
                {folder.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </TooltipProvider>
  );
}
